//
// SELECTORS, these are functions designed to work directly on and with the
// redux data store
//

import createCachedSelector, { LruCacheObject } from 're-reselect';
import { createSelector } from 'reselect';
import { DateTime } from 'luxon';

import { getLogger } from 'utils/logger';
import moment from 'moment/moment';
import { List as ImmutableList, OrderedMap } from 'immutable';
import { noNaN } from 'utils/utils';
import { getOptimalCache, setOptimalCache } from 'data/optimalCacheManager';

import { getTransactionsByAccountId, getTransactionsByAccountIdsSelectors,
  getTransactionsById, getLoadPending as getTransactionsLoadPending,
  getTransactionsByTextFilter, getTransactionsStore } from 'data/transactions/selectors';
import { getAccountsById, getLoadPending as getAccountsLoadPending } from 'data/accounts/selectors';
import { getBalancesByAccountId, includeRecurringTxn } from 'data/accountBalances/selectors';
import {getCategoriesById, getLoadPending as getCategoriesLoadPending} from 'data/categories/selectors';
import {
  isTransferTxn,
  transactionsSortFunction,
  isUncategorizedTxn,
  isUnacceptedScheduledTxn,
  getTxnStateInfo, isSplitTxn,
  getTxnDifferences,
} from 'data/transactions/utils';
import { getFeatureFlags } from 'utils/featureFlags/selectors';
import { getScheduledTransactions } from 'data/scheduledTransactions/scheduledTransactionsSelectors';

import { getReminderSettingInDays } from 'data/accounts/utils';
import { isAcme } from 'isAcme';
import { getPayeesForAccounts } from 'data/payees/selectors';
import { isIncomeCat } from 'data/categories/retrievers';

import { groupTxnListByDate, groupTxnListByAmount, groupTxnListByString } from './grouping';
import { sortTransactions } from './sorting';
import { calcRunningBalances, filterFutureOrDueOverdue } from './utils';

const log = getLogger('data/transactionList/selectors');


//
// Attempt to process newTxn into the cache
// returns the new cache, or NULL if a recalc is needed (can't resolve the cache)
const optimalCacheProcessFn = (cachedValues, newTxn) => {

  let txIndex = -1;
  let sectionKey = null;
  cachedValues.forEach((sec, key) => {
    const index = sec.findIndex((txn) => txn.id === newTxn.id);
    if (index !== -1) {
      sectionKey = key;
      txIndex = index;
    }
  });
  if (txIndex !== -1 && sectionKey) {
    // console.log("FOUND TXN AT ", sectionKey, txIndex, cachedValues.get(sectionKey));
    // check for deleted
    if (newTxn.isDeleted) return cachedValues.set(sectionKey, cachedValues.get(sectionKey).remove(txIndex));
    return cachedValues.setIn([sectionKey, txIndex], newTxn);
  }
  return newTxn.isDeleted ? cachedValues : null;  // if new txn was deleted, and not found, that is ok
};

// Returns TRUE if the changes do NOT cause a recalc of the selector and allow
// for cache replacement
const optimalCacheCompareFn = (oldTxn, newTxn, sortBy, filter) => {
  // determine which fields changed
  const diffs = getTxnDifferences(oldTxn.toJS(), newTxn.toJS());
  // console.log("TXN DIFFS ARE ", diffs);
  if (filter && filter([newTxn]).length === 0) {
    return false;
  }
  // if (filter) console.log("FILTER WSAS APPLIED TO ", newTxn, filter([newTxn]));
  if (isAcme) {
    return (!diffs.includes('postedOn') &&  // always resort on date or amount change
      !(sortBy === 'status' && (diffs.includes('state')) &&
        !(sortBy === 'amount' && diffs.includes('amount')) &&
        !(sortBy === 'reviewed' && diffs.includes('isReviewed')) &&
        !(sortBy === 'category' && diffs.includes('category')) &&
        !(sortBy === 'payee' && diffs.includes('payee'))));
  }
  return (!diffs.includes('postedOn') && (!diffs.includes('amount')) &&  // always resort on date or amount change
    !(sortBy === 'status' && (diffs.includes('state')) &&
      !(sortBy === 'category' && diffs.includes('category')) &&
      !(sortBy === 'reviewed' && diffs.includes('isReviewed')) &&
      !(sortBy === 'payee' && diffs.includes('payee'))));
};

//-----------------------------------------------
//-----------------------------------------------

// getTransactionListForAccountIds
//
// This selector expects the following properties:
//   - an array of accountIds
//   - sortBy property
//   - sortOrder property
//   - filter function that if defined is provided the transaction list to filter. null
//     functions as the identity filter (show all)
//
// Returns an immutable list of transactions from accounts specified by the
// accountIds array.

/*
export const qCreateSelector = (...args) => {
  const selFn = createSelector(...args);

  let lastArgValues = null;

  return (state, props) => {
    const argValues = [];
    args.slice(0, -1).forEach((fn) => argValues.push(fn(state, props)));
    const ret = selFn(state, {...props, lastArgValues });
    lastArgValues = argValues.concat([]);
    return ret;
  }
};
*/

export const getTransactionListForAccountIds = createSelector(
  (state, props) => props.accountIds,
  (state, props) => props.sortBy || 'date',
  (state, props) => props.sortOrder || 'sortDescending',
  (state, props) => props.filterFn,
  (state, props) => props.filterExpression,
  (state, props) => props.overrideTxns,
  (state, props) => props.reminderSetting || 0,
  (state, props) => props.id,
  getTransactionsStore,
  getAccountsById,
  getTransactionsByAccountId,
  getTransactionsByAccountIdsSelectors,
  getBalancesByAccountId,
  getFeatureFlags,

  function combinerClosure() {
    let oldTxnsVal;

    return (
      (
        accountIds,
        sortBy,
        sortOrder,
        filterFn,
        filterExpression,   // used for cacheing, what is the search/filter string?
        overrideTxns,
        reminderSetting,
        id,
        transactionsStore,
        accountsById,
        transactionsByAccountIds,
        transactionsByAccountIdsSelectors,
        balancesById,
        featureFlags,
      ) => {
        // const pt = moment();

        // CACHE STRINGS used for Optimal Cache Manager
        // This first string us used to indicate all elements that should always cause a regeneration (if the combo
        // is not already in the cache)
        const cacheString = `${JSON.stringify(accountIds && accountIds.size > 0 ? accountIds.sort() : 'all')}` +
          `${id}-${sortBy}-${sortOrder}-${overrideTxns && overrideTxns.hashCode()}` +
          `${reminderSetting}-${accountsById.hashCode()}-${featureFlags.hashCode()}` +
          `-${transactionsByAccountIdsSelectors.hashCode()}-${filterExpression}`;

        // THE strict cache string contains the elements that if they are the only changes, then OCM will
        // examine the changes for the given store (transactionStore) one by one to determine if a
        // regeneration is needed (leveraging the CacheCompareFn defined above, and the CacheProcessFn above as well)
        const strictCacheString = `${cacheString}-${transactionsByAccountIds.hashCode()}-${balancesById.hashCode()}`;

        // In some circumstances, the hashCode we use for the cache key will show equality when the actual objects
        // differ, this is a known shortcoming of hashCode() : https://en.wikipedia.org/wiki/Collision_(computer_science)
        // So when the codes are the same, we also validate the actual JS pointer values are the same
        // which must be true since we use immutables.  If they are not, we force a cache break
        const forceCacheBreak = ((oldTxnsVal?.hashCode() === transactionsByAccountIds?.hashCode()) &&
          (oldTxnsVal !== transactionsByAccountIds));


        oldTxnsVal = transactionsByAccountIds;

        // if we are forcing a cache break, then skip cache processing, and go straight to regneeration,
        if (!forceCacheBreak) {
          const cachedValues = getOptimalCache(
            transactionsStore,
            'txList',
            cacheString,
            strictCacheString,
            (oldTxn, newTxn) => optimalCacheCompareFn(oldTxn, newTxn, sortBy, filterFn),
            optimalCacheProcessFn,
            10
          );
          if (cachedValues) return cachedValues;
        }

        log.debug("===================== TXLIST CACHE MISS =======================");

        let accountIdsList = accountIds;
        if (!accountIds || accountIds.length === 0) {
          accountIdsList = accountsById.map((x) => x.id).toList();
        }

        let transactionsForAccountIds;
        if (overrideTxns) {
          transactionsForAccountIds = overrideTxns;
        } else {
          transactionsForAccountIds = new ImmutableList();

          accountIdsList.forEach((accountId) => {

            const acct = accountsById.get(accountId);

            if (acct && !(featureFlags.get('hideLoanTransactions') && acct.type === 'LOAN')) {
              const transactionsForAccountIdSelector =
                transactionsByAccountIdsSelectors.get(accountId);

              const transactionsForAccountId =
                transactionsForAccountIdSelector(transactionsByAccountIds);

              // Filter out reminder transactions as appropriate
              let filteredTransactions = transactionsForAccountId;
              if (!featureFlags.get('scheduledTransactionsInRegister')) {
                filteredTransactions = transactionsForAccountId.filter((txn) => !isUnacceptedScheduledTxn(txn));
              } else {
                // we use the reminderSetting to determine which unaccepted scheduled transactions we show

                let acctReminderSetting = reminderSetting;

                if (acctReminderSetting === 'account' && acct) {
                  acctReminderSetting = getReminderSettingInDays(acct.recurringTxn);
                }

                filteredTransactions =
                  filteredTransactions.filter((txn) =>
                    !isUnacceptedScheduledTxn(txn) || includeRecurringTxn(txn, acctReminderSetting, transactionsForAccountId));  // moment(txn.postedOn).isBefore(lastDate));
              }
              transactionsForAccountIds = transactionsForAccountIds.concat(filteredTransactions);
            }

          });
        }

        let filteredTxns = filterFn ? filterFn(transactionsForAccountIds) : transactionsForAccountIds;
        if (sortBy === 'reviewed') {
          filteredTxns = filteredTxns.filter((txn) => sortOrder === 'ascending' ? !txn.isReviewed : txn.isReviewed);
        }

        // if the app is using the upcomingSection, then filter out any future transactions
        // and DUE/OVERDUE scheduled transactions
        // but do not do this if caller sent in their own txn list (override txns)
        if (featureFlags.get('upcomingSection') && !overrideTxns) {
          filteredTxns = filteredTxns.filter((txn) =>
            moment(txn.postedOn).isSameOrBefore(moment()) && (!isUnacceptedScheduledTxn(txn)));
        }
        const sortedTxns = sortTransactions(filteredTxns, sortBy, sortOrder, featureFlags);

        let txnsWithBalances = sortedTxns;

        if (balancesById.size > 0 && accountIdsList.size === 1 && Number(accountIdsList.first()) !== 0) {
          const balObj = balancesById.get(accountIdsList.first());
          const endingBalance = balObj ? balObj.endingBalance : 0;
          // const { endingBalance } = balancesById.get(accountIdsList.first());
          txnsWithBalances = calcRunningBalances(sortedTxns, sortBy, sortOrder, endingBalance);
        }

        // log.log(txnsWithBalances);

        let groupedTxns;
        switch (sortBy) {

          case 'date':
            groupedTxns = groupTxnListByDate(txnsWithBalances, featureFlags, !isAcme);
            groupedTxns = groupedTxns.sortBy(
              (x, k) => k,
              (a, b) => {
                if (a === b) return 0;
                return (a < b) ? -1 : 1;
              }
            );
            groupedTxns = (sortOrder === 'descending') ? groupedTxns.reverse() : groupedTxns;
            break;

          case 'category':
          case 'payee':
          case 'check':
            groupedTxns = groupTxnListByString(txnsWithBalances, sortBy);
            break;

          case 'reviewed':
            groupedTxns = groupTxnListByDate(txnsWithBalances, featureFlags, true, sortOrder);
            break;

          case 'status':
            groupedTxns = isAcme ? groupTxnListByDate(txnsWithBalances, featureFlags, true, sortOrder) : groupTxnListByString(txnsWithBalances, 'state');
            break;

          case 'amount':
            groupedTxns = groupTxnListByAmount(txnsWithBalances);
            break;

          default:
            groupedTxns = groupTxnListByString(txnsWithBalances, 'payee');
        }

        // log.debug('GROUPED TRANSACTIONS', groupedTxns);

        // log.debug('Selected transactions for accountIds', transactionsForAccountIds);
        // console.log(`RETURNING TX LIST, total time ${moment(pt).diff(moment())}`);

        setOptimalCache(transactionsStore, 'txList', cacheString, strictCacheString, groupedTxns, 10);
        return groupedTxns;
      })
  }(),
);

/*
 * GetTransactionsWithCategoyrSuggestions
 */
export const getTransactionsWithCategorySuggestions = createCachedSelector(
  (state, props) => props.accountIds,
  getTransactionsByAccountId,
  getPayeesForAccounts,
  getAccountsById,
  getTransactionsByAccountIdsSelectors,
  (
    accountIds,
    transactionsByAccountIds,
    payeeList,
    accountsById,
    transactionsByAccountIdsSelectors,
  ) => {

    let accountIdsList = accountIds;
    if (!accountIds || accountIds.length === 0) {
      accountIdsList = accountsById.map((x) => x.id).toList();
    }

    let transactionsForAccountIds;
    transactionsForAccountIds = new ImmutableList();
    accountIdsList.forEach((accountId) => {

      const acct = accountsById.get(accountId);

      if (acct && acct.type !== 'LOAN') {
        const transactionsForAccountIdSelector =
          transactionsByAccountIdsSelectors.get(accountId);

        const transactionsForAccountId =
          transactionsForAccountIdSelector(transactionsByAccountIds);

        transactionsForAccountIds = transactionsForAccountIds.concat(transactionsForAccountId);
      }
    });

    return transactionsForAccountIds.filter((txn) => isUncategorizedTxn(txn) && payeeList.has((txn.payee || '').toLowerCase()));
  }
)(
  (state, props) => `${props.accountIds}`,
  {
    cacheObject: new LruCacheObject({ cacheSize: 3 }),
  },
);

/*
 * GetRecentTransactions
 */
export const getRecentTransactions = createSelector(
  (state) => getAccountsLoadPending(state) || getCategoriesLoadPending(state) || getTransactionsLoadPending(state),
  getTransactionsById,
  getCategoriesById,
  (isLoadPending, txnList, categoriesById) => {
    if (isLoadPending) {
      return new ImmutableList([]);
    }

    // ======== FOR RECENT TX. CARD (SIMPLIFI) =============
    const isRecentSpend = () =>
      txnList.filter((txn) => {
        if (!isSplitTxn(txn)) {
          // returns if is matched scheduled txn or expense
          let cat = categoriesById.get(txn.coa.id);
          const coaType = txn.coa.type;
          if(!cat) cat = 'UNCATEGORIZED';
          if(txn.isExcludedFromReports){
            return false;
          }
          if (cat.type === 'EXPENSE' || 'UNCATEGORIZED' && (cat.type !== 'INCOME') && coaType !== 'ACCOUNT' && coaType !== 'BALANCE_ADJUSTMENT' ) {
            return !(txn.source && txn.source === 'SCHEDULED_TRANSACTION_PENDING');
          }

        }
        else {
          // Check twice in case is split and scheduled, who knows weirder things have happened
          return !(txn.source && txn.source === 'SCHEDULED_TRANSACTION_PENDING');
        }
        return false;
      });

    const getRecent = (transactions, filterByDate = false) => {
      // Transactions filtered differently for acme and quicken/web-first
      if (isAcme){
        const recTxns = isRecentSpend();
        // will return transactions from the past 3 days, sorry for all the parens ¯\_(ツ)_/¯
        const dateFilteredTxns = recTxns.filter((txn) => (
          moment(txn.postedOn).isBefore(moment())
          && moment(txn.postedOn).isAfter(moment().subtract(4, 'days'))));
        return dateFilteredTxns.toList();
      }


      let txns = transactions.filter((txn) => {
        const startDate = moment().subtract(2, 'months');
        const endDate = moment();
        const txnDate = moment(txn.postedOn);
        const isValid = isSplitTxn(txn)
          ? !(txn.source && txn.source === 'SCHEDULED_TRANSACTION_PENDING')
          : txn && !(txn.source && txn.source === 'SCHEDULED_TRANSACTION_PENDING') && txn.coa && txn.coa.type && txn.coa.type !== 'BALANCE_ADJUSTMENT' && (!isTransferTxn(txn) || (Number(txn.amount) < 0));
        const beforeToday = moment(txnDate).isBefore(endDate);
        return isValid && (filterByDate ? beforeToday && moment(txnDate).isAfter(startDate) : beforeToday);
      });
      txns = txns.sort((a, b) => transactionsSortFunction(b, a));
      return txns.toList().slice(0, 5);
    };

    return getRecent(txnList, false);
  }
);

/*
 * GetSortedTransactionsForAccountIds
 */
// THIS IS USED FOR THE MATCHING TXN LIST BOX
// filter out scheduled transactions, filter out matched transactions, sort by date
export const getSortedTransactionsForAccountIds = createSelector(
  (state, props) => props.matchTxn,
  getTransactionsByTextFilter,
  (matchTxn, transactionsForAccountIds) => sortTransactions(transactionsForAccountIds).filter((txn) => {
    const txnState = getTxnStateInfo(txn);
    return (txnState.entry !== 'matched') && (txnState.entry !== 'scheduled') && txnIsClose(txn, matchTxn);
  })
);

function txnIsClose(txn1, txn2) {
  const sameSign = (txn1.amount < 0) === (txn2.amount < 0);
  const reasonableDate = moment(txn1.postedOn).diff(moment(txn2.postedOn), 'days');
  return sameSign && (Math.abs(reasonableDate) < 60);
}

/*
 * GetLesserBilledTransactions
 */
export const getLesserBilledTransactions = createSelector(
  (state, props) => props.isBill,
  getScheduledTransactions,
  getTransactionsByTextFilter,
  (isBill, scheduledTxns, txnList) => {
    const endDate = DateTime.local();
    const startDate = endDate.minus({ months: 6 });
    const txns = txnList.filter((txn) => {
      let keepTransaction = true;
      if (txn.stModelId && scheduledTxns && scheduledTxns.has(txn.stModelId)) {
        keepTransaction = false;
      } else if (isBill !== undefined && (isBill ? txn.amount > 0 : txn.amount <= 0)) {
        keepTransaction = false;
      } else {
        const txnDate = DateTime.fromISO(txn.postedOn);
        keepTransaction = (txnDate >= startDate) && (txnDate <= endDate);
      }
      return keepTransaction;
    });

    return sortTransactions(txns);
  }
);

export const transactionListMetrics = createCachedSelector(
  (state, transactionList) => transactionList,
  (txnList) => {
    let numTxns = 0;
    let sumTxns = 0;
    let earliestDate = moment();
    let reviewedCount = 0;
    let hasScheduledInstances = false;

    txnList.forEach((section, _) => {
      numTxns += section.size;
      section.forEach((txn) => {
        sumTxns += Number(txn.amount);
        if (moment(txn.postedOn).isBefore(earliestDate)) {
          earliestDate = moment(txn.postedOn);
        }
        reviewedCount += (txn.isReviewed ? 1 : 0);
        hasScheduledInstances = hasScheduledInstances || isUnacceptedScheduledTxn(txn);
      });
    });
    return { numTxns, sumTxns, earliestDate, reviewedCount, hasScheduledInstances };
  }
)(
  (state, props) => props.accountIds ? props.accountIds.toString() : 'all',
  {
    cacheObject: new LruCacheObject({ cacheSize: 10 }),
  }
);


// ## ================================================================================================ ##
//
// ##                              Chris Selectors   (OH NO)  RUN TO THE HILLS!!!! RUN FOR YOUR LIFE!! ##  (Iron Maiden) \,,/
//
// ## ================================================================================================ ##


export const getProjectedTxnList = createCachedSelector(
  (state, props) => props.accountIds,
  (state, props) => props.overrideTxns,
  getAccountsById,
  getBalancesByAccountId,
  getTransactionsByAccountId,
  getTransactionsByAccountIdsSelectors,
  getFeatureFlags,
  (
    accountIds,
    overrideTxns,
    accountsById,
    balancesById,
    transactionsByAccountId,
    transactionsByAccountIdsSelectors,
    featureFlags,
  ) => {
    const sortBy = 'date';
    const sortOrder = 'ascending';

    let accountIdsList = accountIds;
    if (!accountIds || accountIds.length === 0) {
      accountIdsList = accountsById.map((x) => x.id).toList();
    }

    const transactionsForAccountIds = overrideTxns || getFutureTxnsForAccountIds({
      accountIdsList,
      accountsById,
      transactionsByAccountId,
      transactionsByAccountIdsSelectors,
      featureFlags,
    });

    return sortTransactions(transactionsForAccountIds, sortBy, sortOrder);
  }
)(
  (state, props) => {
    let cacheString = `${JSON.stringify(props.accountIds && props.accountIds.size > 0 ? props.accountIds.sort() : 'all')}`;
    cacheString = `${props.id}-${cacheString}-${props.overrideTxns && props.overrideTxns.hashCode()}`;

    return cacheString;
  },
  {
    cacheObject: new LruCacheObject({ cacheSize: 8 }),
  }
);

export const getLatestProjectedTxnList = createSelector(
  getAccountsById,
  getBalancesByAccountId,
  getTransactionsByAccountId,
  getTransactionsByAccountIdsSelectors,
  getFeatureFlags,
  (state, props) => props.maxTxns,
  getScheduledTransactions,
  (
    accountsById,
    balancesById,
    transactionsByAccountId,
    transactionsByAccountIdsSelectors,
    featureFlags,
    maxTxns,
  ) => {
    const sortBy = 'date';
    const sortOrder = 'ascending';
    const accountIdsList = accountsById.map((x) => x.id).toList();
    const transactionsForAccountIds = getFutureTxnsForAccountIds({
      accountIdsList,
      accountsById,
      transactionsByAccountId,
      transactionsByAccountIdsSelectors,
      featureFlags,
    });

    const filterTxns = isAcme ? transactionsForAccountIds.filter((txn) => DateTime.fromISO(txn.postedOn) > DateTime.local()) : transactionsForAccountIds;

    let upcomingTxns = sortTransactions(filterTxns, sortBy, sortOrder);
    if (maxTxns && upcomingTxns.size > maxTxns) {
      upcomingTxns = upcomingTxns.slice(0, maxTxns);
    }
    return upcomingTxns;
  }
);

export const getFutureTxnsForAccountIds = createSelector(
  (props) => props.accountIdsList,
  (props) => props.accountsById,
  (props) => props.transactionsByAccountId,
  (props) => props.transactionsByAccountIdsSelectors,
  (props) => props.featureFlags,
  (
    accountIdsList,
    accountsById,
    transactionsByAccountIds,
    transactionsByAccountIdsSelectors,
    featureFlags,
  ) => {
    let txnList = new ImmutableList();
    let filteredTransactions;
    const filter = '';
    const sortBy = 'date';
    const sortOrder = 'ascending';

    accountIdsList.forEach((accountId) => {

      const acct = accountsById.get(accountId);

      if (acct && !(featureFlags.get('hideLoanTransactions') && acct.type === 'LOAN')) {
        const transactionsForAccountIdSelector = transactionsByAccountIdsSelectors.get(accountId);

        const transactionsForAccountId =
          transactionsForAccountIdSelector(transactionsByAccountIds, {
            sortBy,
            sortOrder,
            filter,
          });
        filteredTransactions = transactionsForAccountId.filter((txn) => filterFutureOrDueOverdue(txn));
        txnList = txnList.concat(filteredTransactions);
      }
    });
    return txnList;
  }
);

export const futureFilteredTxns = createSelector(
  (txns) => txns,
  (txns, date) => date,
  (txns, date, schTxns) => schTxns,
  (txns, date, schTxns) => {
    const endDate = DateTime.local().plus({ days: date });

    return txns.filter((txn) => {
      const txnDate = DateTime.fromISO(txn.postedOn);
      if(schTxns) {
        return (
          (txn.source === 'USER_ENTERED' && txnDate.diff(DateTime.local(), 'days').toObject().days >= 0) || txn.source === 'SCHEDULED_TRANSACTION_PENDING'
        );
      };
      return (
        (txn.source === 'USER_ENTERED' &&
        txnDate.diff(DateTime.local(), 'days').toObject().days >= 0) &&  // same day or future
        (txnDate < endDate) ||  // not past dateRange or ignore this bool statement
        txn.source === 'SCHEDULED_TRANSACTION_PENDING' // adding this so we can have the 'waiting' button
      );
    });
  }
);

export const getDateGroupedTransactions = createSelector(
  (txns, date, schTxns) => futureFilteredTxns(txns, date, schTxns),
  (futureTxns) => futureTxns.groupBy((txn) => DateTime.fromISO(txn.postedOn).toISODate())
);

const getPastDateMap = createSelector(
  (beforeDays)=> beforeDays,
  (beforeDays) => {
    const endDate = DateTime.local().minus({ days: 1 });
    let dateMap = OrderedMap();
    let currentDate = endDate;
    let currentIndex = 0;
    while (currentIndex < beforeDays - 1) {
      dateMap = dateMap.set(currentDate.toISODate(), currentIndex);
      currentDate = currentDate.minus({ days: 1 });
      currentIndex += 1;
    }
    return dateMap;
  }
);

const getPresentDateMap = createSelector(
  (days) => days,
  (days) => {
    const startDate = DateTime.local();
    // const endDate = DateTime.local().plus({days});

    let dateMap = OrderedMap();
    let currentDate = startDate;
    let currentIndex = 0;
    while (currentIndex < days ) {
      dateMap = dateMap.set(currentDate.toISODate(), currentIndex);
      currentDate = currentDate.plus({ days: 1 });
      currentIndex += 1;
    }
    dateMap = dateMap.set(currentDate.toISODate(), currentIndex);

    return dateMap;
  }
);
/*
export const ccs = (arguments) => {

  const localArgs = arguments;
  return (cacheArgs) => {
    // do cache stuff with cache args
    // either return cache values based on "arguments" OR
    const selFn = (localArgs) => {
      return localArgs[n](localArgs.slice(-1));
    }

    // cal selFn
  }
}
*/
export const getRecentAccountTransactions = createSelector(
  getTransactionsByAccountId,
  (state, accountId) => accountId,
  (txnsByAccountId, accountId) => {
    const today = DateTime.local().startOf('day');
    const pastCutoff = today.minus({days: 10});
    const ungroupedTxns = txnsByAccountId && txnsByAccountId.get(accountId).filter((txn) => {
      const txnDate = txn.postedOn && DateTime.fromISO(txn.postedOn);
      return (txnDate && txnDate < today && txnDate > pastCutoff);
    });
    return ungroupedTxns.groupBy((txn) => DateTime.fromISO(txn.postedOn).toISODate());
  }
);

export const getAMChartData = createCachedSelector(
  (state, props) => getDateGroupedTransactions(props.futureTxns, props.dateRange, false),
  (state, props) => getRecentAccountTransactions(state, props.accountIds.first()),
  () => getPastDateMap(10),
  (state, props) => getPresentDateMap(props.dateRange),
  (state, props) => props.dateRange,
  (state, props) => props.accountIds,
  getBalancesByAccountId,
  getAccountsById,
  (groupedTxns, pastGroupedTxns, beforeDateMap, dateMap, dateRange, accountIds, balancesById, accountsById) => {
    const chartData = [];
    let currentBalance = 0;
    let todayBalance = 0;
    if (balancesById.size > 0 && accountIds.size === 1 && Number(accountIds.first()) !== 0) {
      const balObj = balancesById.get(accountIds.first());
      currentBalance = balObj ? balObj.currentBalance : 0;
      todayBalance = balObj ? balObj.currentBalance : 0;
    }

    beforeDateMap.forEach((value, key) => {
      const currTxnGroup = pastGroupedTxns.get(key);
      if (currTxnGroup) {
        currTxnGroup.forEach((txn) => {
          todayBalance -= noNaN(txn.amount);
        });
        chartData.push({balance: Math.round(todayBalance), date: key, txns: null, disabled: true, noIncome: true });
      } else {
        chartData.push({balance: Math.round(todayBalance), date: key, txns: null, disabled: true, noIncome: true });
      }
    });
    chartData.reverse();

    dateMap.forEach((value, key) => {
      const txnGroup = groupedTxns.get(key);
      let noIncome = true;
      if (txnGroup) {
        txnGroup.forEach((txn) => {
          currentBalance += noNaN(txn.amount);
          if(isIncomeCat(txn.coa?.id)) {
            noIncome = false;
          }
        });
        chartData.push({balance: Math.round(currentBalance), date: key, txns: txnGroup, disabled: !noIncome, noIncome });
      } else {
        chartData.push({balance: Math.round(currentBalance), date: key, txns: null, disabled: true, noIncome });
      }
    });

    const thisAccount = accountsById.get(accountIds.first());
    const credit = thisAccount && thisAccount.type === 'CREDIT';

    return {
      chartData,
      credit,
    };
  }
)(
  (txns, dateRange, accountIds) => accountIds ? `${accountIds.hashCode()}and${dateRange}` : `alland${dateRange}`,
  {
    cacheObject: new LruCacheObject({ cacheSize: 5 }),
  }
);

export const processCalendarTxns = createSelector(
  (txns) => txns,
  (txns) => txns.groupBy(() => '9999-99:Pending')
);
